<script
  setup
  lang="ts"
  generic="T extends { id: string | number; badge?: string }, E extends T | T[]"
>
import { isArray } from "lodash-es";
import { v4 as uuid } from "uuid";
import { computed } from "vue";

import type { ListItemVariant } from "@/components/ui/ListItem/ListItem.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListFooter from "@/components/ui/MenuList/MenuListFooter.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import { terminateEvent } from "@/utils/dom/dom";

import MenuListItem from "../MenuList/MenuListItem.vue";
import MenuListTitle from "../MenuList/MenuListTitle.vue";

const props = defineProps<{
  header?: string;
  values: T[];
  modelValue?: E | null;
  variant?: ListItemVariant;
  scrollable?: boolean;
  // Disables the entire list - all items become non-interactive
  disabled?: boolean;
  ariaLabelledby?: string;
  // Per-item disable function - allows disabling specific items while keeping others enabled
  isItemDisabled?: (item: T) => boolean;
  // Tooltip message shown when hovering over a disabled item
  disabledMessage?: string;
}>();

const emit = defineEmits<(e: "update:modelValue", value: E) => void>();

const isMulti = isArray(props.modelValue);
const isClearable = computed(() => isMulti && props.modelValue.length > 0);

const titleId = uuid();

function isDisabled(item: T) {
  return props.disabled || props.isItemDisabled?.(item);
}

function select(value: T, event: PointerEvent) {
  if (isDisabled(value)) return;

  emit("update:modelValue", (isMulti ? newValue() : value) as E);

  function newValue() {
    const modelValues = props.modelValue as T[];
    terminateEvent(event);
    const pos = modelValues.findIndex((val) => val.id === value.id);
    return pos >= 0
      ? [...modelValues.slice(0, pos), ...modelValues.slice(pos + 1)]
      : [...modelValues, value];
  }
}

function clear() {
  if (props.disabled) return;
  emit("update:modelValue", [] as unknown as E);
}

function isActive(value: T) {
  return !isMulti && value.id === props.modelValue?.id;
}

function isSelected(value: T, aria?: boolean) {
  if (isMulti) {
    return props.modelValue.some((val) => val.id === value.id);
  } else if (aria) {
    // For aria-selected, we want to identify the selected item even if single-select
    return props.modelValue?.id === value.id;
  }

  return isActive(value);
}
</script>

<template>
  <MenuList selectable :multiselect="isMulti">
    <MenuListHeader v-if="header" :id="titleId">
      <MenuListTitle>{{ header }}</MenuListTitle>
    </MenuListHeader>
    <MenuListItems
      v-focus-trap
      :aria-labelledby="ariaLabelledby || (header && titleId) || undefined"
    >
      <MenuListItem
        v-for="value in values"
        :key="value.id"
        role="option"
        :selected="isSelected(value)"
        :variant
        :disabled="isDisabled(value)"
        :aria-selected="isSelected(value, true)"
        :badge="value.badge"
        :tooltip="isItemDisabled?.(value) ? disabledMessage : undefined"
        class="menu-list-item"
        @click="select(value, $event)"
      >
        <template v-if="$slots.before" #before>
          <slot :item="value" :selected="isSelected(value)" name="before" />
        </template>
        <template v-if="$slots.default" #default>
          <slot :item="value" :selected="isSelected(value)" />
        </template>
        <template v-if="$slots.after" #after>
          <slot :item="value" name="after" />
        </template>
      </MenuListItem>
    </MenuListItems>
    <template v-if="isMulti">
      <MenuListFooter :disabled="!isClearable || disabled" @click="clear" />
    </template>
  </MenuList>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";

.check-container {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: colors.$text;
}

.clear {
  font-size: 12px;
  color: colors.$text;
  justify-content: center;
}
</style>
